import type { PendingDialog } from './internal-types.js';

export function getPendingDialogMessage(dialogs: readonly PendingDialog[]): string | null {
  if (dialogs.length === 0) return null;

  const dialog = dialogs[0];

  return (
    `DIALOG BLOCKED ACTION: A ${dialog.type} dialog appeared with message: "${dialog.message}". ` +
    `The dialog was automatically dismissed, so the action was CANCELLED and did NOT take effect.\n\n` +
    `RECOVERY (one tool call): browser_handle_dialog with action:"accept" (or "dismiss") AND ` +
    `replayLastAction:true. This pre-arms the handler and re-issues your last action so the ` +
    `dialog is handled inline.\n\n` +
    `THEN: when you record the playbook step for this action, set expectedDialogs:"accept" ` +
    `(or "dismiss") on it so future replays pre-arm automatically and you don't have to ` +
    `recover at runtime. Do NOT record a separate handle_native_dialog step — expectedDialogs ` +
    `keeps the pre-arm and the triggering action together.\n\n` +
    `Legacy two-step (only if you need to change the action between calls): ` +
    `browser_handle_dialog{action:"accept"}, then re-issue the trigger yourself.`
  );
}

export function consumePendingDialogMessage(dialogs: PendingDialog[]): string | null {
  const msg = getPendingDialogMessage(dialogs);
  if (msg) {
    dialogs.shift();
  }
  return msg;
}

/**
 * Pop the first native dialog queued *since* a given queue depth, consuming all
 * newly-queued dialogs so a `run_code` block that triggered one does not wedge
 * the next call. Returns null if nothing new was queued.
 */
export function consumeRunCodeDialog(dialogs: PendingDialog[], since: number): PendingDialog | null {
  if (dialogs.length <= since) return null;
  const first = dialogs[since];
  dialogs.length = since;
  return first;
}

export function buildRunCodeDialogMessage(dialog: Pick<PendingDialog, 'type' | 'message'>): string {
  const message =
    dialog.message.length > 200 ? `${dialog.message.slice(0, 200)}…` : dialog.message;
  return (
    `NATIVE DIALOG TRIGGERED: your code triggered a ${dialog.type} dialog ("${message}"), which was ` +
    `auto-dismissed — so the action was CANCELLED and did NOT take effect. To handle it, arm the dialog ` +
    `BEFORE the triggering action in the same block, then retry:\n` +
    `  dialog("accept"); await page.getByRole(...).click();\n` +
    `Use dialog("dismiss") to cancel it, or dialog("accept", "text") to answer a prompt.`
  );
}
